import { Company, Portfolio } from '@app/models';
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  memo,
} from 'react';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar';
import { Separator } from 'components/ui/separator';
import { Badge } from 'components/ui/badge';
import { Star, MapPin, Briefcase, Calendar, Building } from 'lucide-react';
import { calculatePortfolios } from '@app/helpers';
import avatar from 'assets/images/avatar.png';
import { useUserContext } from '@app/providers';
import { getFileById } from '@app/api';
import { cn } from 'lib/utils';

interface CardProps extends Partial<Company> {
  onClick?: () => void;
  portfolios: Portfolio[];
  invertBgColorCard?: boolean;
}
const InfoRow = ({
  label,
  value,
  icon: Icon,
  className = '',
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}) => (
  <div className={cn('flex flex-col space-y-1.5 min-w-0 group', className)}>
    <div className="flex items-center gap-1.5">
      {Icon && (
        <Icon className="w-3 h-3 text-muted-foreground/60 group-hover:text-muted-foreground transition-colors" />
      )}
      <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
        {label}
      </span>
    </div>
    <div className="text-sm font-medium text-foreground break-words hyphens-auto pl-0">
      {value}
    </div>
  </div>
);

const StarRating = ({ rating = 0 }: { rating?: number }) => (
  <div className="flex items-center gap-2">
    <Badge
      variant="secondary"
      className="text-xs font-medium bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800"
    >
      {rating.toFixed(1)}
    </Badge>
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={14}
          className={cn(
            'transition-all duration-300 ease-in-out',
            i <= rating
              ? 'text-amber-500 fill-amber-500 scale-100'
              : 'text-muted-foreground/30 scale-90'
          )}
        />
      ))}
    </div>
  </div>
);

const CompanyAvatar = ({
  imageIds,
  placeholder,
  companyName,
}: {
  imageIds: string[];
  placeholder: string;
  companyName: string;
}) => {
  const [src, setSrc] = useState<string>(placeholder);
  const [isLoading, setIsLoading] = useState(false);
  const mainImageId = imageIds && imageIds.length > 0 ? imageIds[0] : null;
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    if (mainImageId) {
      setIsLoading(true);
      getFileById(mainImageId)
        .then((blob) => {
          if (!isMounted) return;
          const url = URL.createObjectURL(blob);
          urlRef.current = url;
          setSrc(url);
        })
        .catch(() => setSrc(placeholder))
        .finally(() => setIsLoading(false));
    } else {
      setSrc(placeholder);
    }
    return () => {
      isMounted = false;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
    };
  }, [mainImageId, placeholder]);

  const initials = companyName
    .split(' ')
    .map((name) => name.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="relative">
      <Avatar className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl border-2 border-border/50 shadow-sm transition-all duration-300 hover:shadow-md hover:border-border group-hover:scale-105">
        <AvatarImage
          src={src}
          className={cn(
            'object-cover transition-opacity duration-300',
            isLoading && 'opacity-50'
          )}
        />
        <AvatarFallback className="bg-gradient-to-br from-blue-50 to-indigo-100 text-blue-600 font-semibold text-sm dark:from-blue-950 dark:to-indigo-900 dark:text-blue-300">
          {initials || '?'}
        </AvatarFallback>
      </Avatar>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

function CompanyCard(props: CardProps) {
  const {
    name,
    kato,
    rating,
    onClick,
    portfolios,
    avatarFilesIds,
    invertBgColorCard = false,
    okeds,
  } = props;

  const { user } = useUserContext();
  const [isHovered, setIsHovered] = useState(false);

  const onClickCard = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onClick) return;
      onClick();
    },
    [onClick]
  );

  const nameSafe = name || 'Не указано';
  const katoSafe = kato?.nameRu || 'Не указан';
  const okedSafe = okeds && okeds.length > 0 ? okeds[0].nameRu : 'Не указана';
  const images = useMemo(() => {
    if (avatarFilesIds) {
      return avatarFilesIds.split(',');
    }
    return [];
  }, [avatarFilesIds]);

  const portfolioDate = useMemo(() => {
    return calculatePortfolios(portfolios);
  }, [portfolios]);

  const oked = useMemo(() => {
    if (!okeds || okeds.length === 0) {
      return 'Не указана';
    }
    return okeds[0].nameRu;
  }, [okeds]);

  return (
    <Card
      className={cn(
        'w-full group cursor-pointer overflow-hidden',
        'transition-all duration-300 ease-out',
        'hover:shadow-lg hover:shadow-black/5',
        'hover:-translate-y-1 hover:scale-[1.01]',
        'border-border/50 hover:border-border',
        invertBgColorCard ? 'bg-muted/30' : 'bg-card'
      )}
      onClick={onClickCard}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col md:hidden space-y-4">
          <div className="flex items-center space-x-3">
            <CompanyAvatar
              imageIds={images}
              placeholder={avatar}
              companyName={nameSafe}
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base leading-tight break-words group-hover:text-primary transition-colors">
                {nameSafe}
              </h3>
              <div className="flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3 text-muted-foreground/60" />
                <p className="text-sm text-muted-foreground">{katoSafe}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <InfoRow
              label="Сфера деятельности"
              value={okedSafe}
              icon={Briefcase}
            />
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="На рынке" value={portfolioDate} icon={Calendar} />
              <InfoRow
                label="Рейтинг"
                value={<StarRating rating={rating || 0} />}
              />
            </div>
          </div>

          {user?.authenticated && (
            <Button
              onClick={onClick}
              className="w-full transition-all duration-300 hover:shadow-md"
              size="sm"
            >
              Подробнее
            </Button>
          )}
        </div>

        <div className="hidden md:flex xl:hidden items-center space-x-6">
          <div className="flex-shrink-0">
            <CompanyAvatar
              imageIds={images}
              placeholder={avatar}
              companyName={nameSafe}
            />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg leading-tight break-words mb-2 group-hover:text-primary transition-colors">
              {nameSafe}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label="Регион" value={katoSafe} icon={MapPin} />
              <InfoRow
                label="Сфера деятельности"
                value={okedSafe}
                icon={Briefcase}
              />
              <InfoRow label="На рынке" value={portfolioDate} icon={Calendar} />
              <InfoRow
                label="Рейтинг"
                value={<StarRating rating={rating || 0} />}
              />
            </div>
          </div>

          {user?.authenticated && (
            <div className="flex-shrink-0">
              <Button
                onClick={onClick}
                size="sm"
                className="transition-all duration-300 hover:shadow-md"
              >
                Подробнее
              </Button>
            </div>
          )}
        </div>

        <div className="hidden xl:flex items-center space-x-6">
          <div className="flex-shrink-0">
            <CompanyAvatar
              imageIds={images}
              placeholder={avatar}
              companyName={nameSafe}
            />
          </div>

          <Separator
            orientation="vertical"
            className={cn(
              'h-20 transition-all duration-300',
              isHovered && 'bg-border'
            )}
          />

          <div className="flex-1 min-w-0">
            <div className="grid grid-cols-5 gap-6">
              <InfoRow
                label="Наименование"
                value={
                  <span className="group-hover:text-primary transition-colors font-medium">
                    {nameSafe}
                  </span>
                }
                icon={Building}
              />
              <InfoRow label="Регион" value={katoSafe} icon={MapPin} />
              <InfoRow
                label="Сфера деятельности"
                value={okedSafe}
                icon={Briefcase}
              />
              <InfoRow label="На рынке" value={portfolioDate} icon={Calendar} />
              <InfoRow
                label="Рейтинг"
                value={<StarRating rating={rating || 0} />}
              />
            </div>
          </div>

          {user?.authenticated && (
            <div className="flex-shrink-0">
              <Button
                onClick={onClick}
                size="sm"
                className="w-28 transition-all duration-300 hover:shadow-md"
              >
                Подробнее
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default memo(CompanyCard);
